import { getFeatureFlagValue } from '@citrite/feature-flags';
import { DataVersion } from '@sharefiledev/projects-pilet-sdk';
import { Preset, UserRole } from '@sharefiledev/sharing-pilet-sdk';
import { FeatureFlag } from './featureFlags';
import { t } from './translate';

export const evaluateLocalizedPermissionConfig = (
	projectVersion?: DataVersion,
	verticalContextKey?: string
) => {
	const availablePermissionSets = [
		{
			key: 'project-view',
			description: t('PermissionsConfig.projects.canViewProject', {
				context: verticalContextKey,
			}),
			sourceLabel: t('ProjectsListView.title', { context: verticalContextKey }),
			permissions: ['project:read'],
		},
		{
			key: 'project-view-all',
			description: t('PermissionsConfig.projects.canViewAllProjectContent', {
				context: verticalContextKey,
			}),
			sourceLabel: t('ProjectsListView.title', { context: verticalContextKey }),
			permissions: ['*:read'],
		},
		{
			key: 'project-content-create',
			description: t('PermissionsConfig.projects.canCreateContent'),
			sourceLabel: t('ProjectsListView.title', { context: verticalContextKey }),
			permissions: ['*:create'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'project-modify',
			description: t('PermissionsConfig.projects.canModifyProject', {
				context: verticalContextKey,
			}),
			sourceLabel: t('ProjectsListView.title', { context: verticalContextKey }),
			permissions: ['project:modify'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'project-modify-all',
			description: t('PermissionsConfig.projects.canModifyContent'),
			sourceLabel: t('ProjectsListView.title', { context: verticalContextKey }),
			permissions: ['*:modify'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'project-share',
			description: t('PermissionsConfig.projects.canShareProject', {
				context: verticalContextKey,
			}),
			sourceLabel: t('ProjectsListView.title', { context: verticalContextKey }),
			permissions: ['project:share'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'project-share-all',
			description: t('PermissionsConfig.projects.canShareProjectAndAllContent', {
				context: verticalContextKey,
			}),
			sourceLabel: t('ProjectsListView.title', { context: verticalContextKey }),
			permissions: ['*:share'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'project-delete-all',
			description: t('PermissionsConfig.projects.canDeleteProjectAndAllContent', {
				context: verticalContextKey,
			}),
			sourceLabel: t('ProjectsListView.title', { context: verticalContextKey }),
			permissions: ['*:delete'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
			requiredRoles: [UserRole.Employee],
		},
		...getFilesPermissionSet(),
		{
			key: 'conversation-view',
			description: t('PermissionsConfig.conversation.canViewAllComments'),
			sourceLabel: t('PermissionsConfig.conversation.sourceLabel'),
			permissions: ['conversation:read'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'conversation-create',
			description: t('PermissionsConfig.conversation.canAddComments'),
			sourceLabel: t('PermissionsConfig.conversation.sourceLabel'),
			permissions: ['conversation:create'],
			requireOneOfKeys: ['conversation-view', 'project-view-all'],
		},
		{
			key: 'conversation-delete',
			description: t('PermissionsConfig.conversation.canDeleteAllComments'),
			sourceLabel: t('PermissionsConfig.conversation.sourceLabel'),
			permissions: ['conversation:delete'],
			requireOneOfKeys: ['conversation-view', 'project-view-all'],
			requiredRoles: [UserRole.Employee],
		},
		{
			key: 'documentrequest-view',
			description: t('PermissionsConfig.documentRequest.canViewAllDocumentRequests'),
			sourceLabel: t('PermissionsConfig.documentRequest.sourceLabel'),
			permissions: ['requestlist:read'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'documentrequest-view-drafts',
			description: t('PermissionsConfig.documentRequest.canViewAllDrafts'),
			sourceLabel: t('PermissionsConfig.documentRequest.sourceLabel'),
			permissions: ['requestlist:read', 'requestlist:modify'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'documentrequest-create',
			description: t('PermissionsConfig.documentRequest.canCreateDocumentRequests'),
			sourceLabel: t('PermissionsConfig.documentRequest.sourceLabel'),
			permissions: ['requestlist:create'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
			requiredRoles: [UserRole.Employee],
		},
		{
			key: 'documentrequest-modify',
			description: t('PermissionsConfig.documentRequest.canModifyAllDocumentRequests'),
			sourceLabel: t('PermissionsConfig.documentRequest.sourceLabel'),
			permissions: ['requestlist:modify'],
			requireOneOfKeys: ['documentrequest-view', 'project-view-all'],
			requiredRoles: [UserRole.Employee],
		},
		{
			key: 'documentrequest-delete',
			description: t('PermissionsConfig.documentRequest.canDeleteAllDocumentRequests'),
			sourceLabel: t('PermissionsConfig.documentRequest.sourceLabel'),
			permissions: ['requestlist:delete'],
			requireOneOfKeys: ['documentrequest-view', 'project-view-all'],
			requiredRoles: [UserRole.Employee],
		},
		{
			key: 'todo-view',
			description: t('PermissionsConfig.tasks.canViewAllTasks'),
			sourceLabel: t('PermissionsConfig.tasks.sourceLabel'),
			permissions: ['todo:read'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'todo-create',
			description: t('PermissionsConfig.tasks.canCreateTasks'),
			sourceLabel: t('PermissionsConfig.tasks.sourceLabel'),
			permissions: ['todo:create'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'todo-modify',
			description: t('PermissionsConfig.tasks.canModifyAllTasks'),
			sourceLabel: t('PermissionsConfig.tasks.sourceLabel'),
			permissions: ['todo:modify'],
			requireOneOfKeys: ['todo-view', 'project-view-all'],
		},
		{
			key: 'todo-delete',
			description: t('PermissionsConfig.tasks.canDeleteAllTasks'),
			sourceLabel: t('PermissionsConfig.tasks.sourceLabel'),
			permissions: ['todo:delete'],
			requireOneOfKeys: ['todo-view', 'project-view-all'],
			requiredRoles: [UserRole.Employee],
		},
		{
			key: 'inforeq-view',
			description: t(
				'PermissionsConfig.informationRequests.canViewAllInformationRequests'
			),
			sourceLabel: t('PermissionsConfig.informationRequests.sourceLabel'),
			permissions: ['inforeq:read'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'inforeq-create',
			description: t(
				'PermissionsConfig.informationRequests.canCreateInformationRequests'
			),
			sourceLabel: t('PermissionsConfig.informationRequests.sourceLabel'),
			permissions: ['inforeq:create'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'inforeq-modify',
			description: t(
				'PermissionsConfig.informationRequests.canModifyAllInformationRequests'
			),
			sourceLabel: t('PermissionsConfig.informationRequests.sourceLabel'),
			permissions: ['inforeq:modify'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['inforeq-view', 'project-view-all'],
		},
		{
			key: 'inforeq-delete',
			description: t(
				'PermissionsConfig.informationRequests.canDeleteAllInformationRequests'
			),
			sourceLabel: t('PermissionsConfig.informationRequests.sourceLabel'),
			permissions: ['inforeq:delete'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['inforeq-view', 'project-view-all'],
		},
		...getAutomationsPermissionSet(),
		{
			key: 'datatable-view',
			description: t('PermissionsConfig.datatables.canViewAllDataTables'),
			sourceLabel: t('PermissionsConfig.datatables.sourceLabel'),
			permissions: ['datatable:read'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'datatable-create',
			description: t('PermissionsConfig.datatables.canCreateDataTables'),
			sourceLabel: t('PermissionsConfig.datatables.sourceLabel'),
			permissions: ['datatable:create'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
			requiredRoles: [UserRole.Employee],
		},
		{
			key: 'datatable-modify',
			description: t('PermissionsConfig.datatables.canModifyAllDataTables'),
			sourceLabel: t('PermissionsConfig.datatables.sourceLabel'),
			permissions: ['datatable:modify'],
			requireOneOfKeys: ['datatable-view', 'project-view-all'],
			requiredRoles: [UserRole.Employee],
		},
		{
			key: 'datatable-delete',
			description: t('PermissionsConfig.datatables.canDeleteAllDataTables'),
			sourceLabel: t('PermissionsConfig.datatables.sourceLabel'),
			permissions: ['datatable:delete'],
			requireOneOfKeys: ['datatable-view', 'project-view-all'],
			requiredRoles: [UserRole.Employee],
		},
		{
			key: 'reviewapproval-view',
			description: t('PermissionsConfig.reviewapprovals.canViewAllReviewApprovals'),
			sourceLabel: t('PermissionsConfig.reviewapprovals.sourceLabel'),
			permissions: ['reviewapproval:read'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'reviewapproval-create',
			description: t('PermissionsConfig.reviewapprovals.canCreateReviewApprovals'),
			sourceLabel: t('PermissionsConfig.reviewapprovals.sourceLabel'),
			permissions: ['reviewapproval:create'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['reviewapproval-view', 'project-view-all'],
		},
		{
			key: 'reviewapproval-modify',
			description: t('PermissionsConfig.reviewapprovals.canModifyAllReviewApprovals'),
			sourceLabel: t('PermissionsConfig.reviewapprovals.sourceLabel'),
			permissions: ['reviewapproval:modify'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['reviewapproval-view', 'project-view-all'],
		},
		{
			key: 'reviewapproval-delete',
			description: t('PermissionsConfig.reviewapprovals.canDeleteAllReviewApprovals'),
			sourceLabel: t('PermissionsConfig.reviewapprovals.sourceLabel'),
			permissions: ['reviewapproval:delete'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['reviewapproval-view', 'project-view-all'],
		},
		{
			key: 'reviewapproval-revision-view',
			description: t('PermissionsConfig.reviewapprovals.canViewAllRevisions'),
			sourceLabel: t('PermissionsConfig.reviewapprovals.sourceLabel'),
			permissions: ['reviewapproval_revision:read'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'reviewapproval-revision-create',
			description: t('PermissionsConfig.reviewapprovals.canCreateRevisions'),
			sourceLabel: t('PermissionsConfig.reviewapprovals.sourceLabel'),
			permissions: ['reviewapproval_revision:create'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['reviewapproval-revision-view', 'project-view-all'],
		},
		{
			key: 'reviewapproval-revision-modify',
			description: t('PermissionsConfig.reviewapprovals.canModifyAllRevisions'),
			sourceLabel: t('PermissionsConfig.reviewapprovals.sourceLabel'),
			permissions: ['reviewapproval_revision:modify'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['reviewapproval-revision-view', 'project-view-all'],
		},
		{
			key: 'reviewapproval-revision-delete',
			description: t('PermissionsConfig.reviewapprovals.canDeleteAllRevisions'),
			sourceLabel: t('PermissionsConfig.reviewapprovals.sourceLabel'),
			permissions: ['reviewapproval_revision:delete'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['reviewapproval-revision-view', 'project-view-all'],
		},
	];

	if (projectVersion && projectVersion > DataVersion.Original) {
		const exportPermissionSets = [
			{
				key: 'project-export-all',
				description: t('PermissionsConfig.projects.canExportAllContent', {
					context: verticalContextKey,
				}),
				sourceLabel: t('ProjectsListView.title', { context: verticalContextKey }),
				permissions: ['*:export'],
				requireOneOfKeys: ['project-view', 'project-view-all'],
			},
			getFileExportPermissionSet(),
		];
		availablePermissionSets.push(...exportPermissionSets);
	}

	return {
		availablePermissionSets,
		presets: getPresets(projectVersion, verticalContextKey),
		permissionSourceGroups: [
			{
				label: t('PermissionSourceGroups.projectLabel', { context: verticalContextKey }),
				description: t('PermissionSourceGroups.projectPermissionsDescription', {
					context: verticalContextKey,
				}),
				sourceLabels: [t('ProjectsListView.title', { context: verticalContextKey })],
			},
			{
				label: t('PermissionSourceGroups.contentLabel'),
				description: t('PermissionSourceGroups.contentPermissionsDescription'),
				sourceLabels: [
					t('FilePermissions.ShareEntity'),
					t('PermissionsConfig.conversation.sourceLabel'),
					t('PermissionsConfig.documentRequest.sourceLabel'),
					t('PermissionsConfig.informationRequests.sourceLabel'),
					t('PermissionsConfig.tasks.sourceLabel'),
					t('PermissionsConfig.datatables.sourceLabel'),
					t('PermissionsConfig.reviewapprovals.sourceLabel'),
					t('PermissionsConfig.automations.sourceLabel'),
				],
			},
		],
	};
};

const getPresets = (
	projectVersion?: DataVersion,
	verticalContextKey?: string
): Preset[] => {
	const owner: Preset = {
		rid: 'sharefile:permission_preset-owner',
		name: t('ProjectRoles.owner'),
		permissions: ['*:create', '*:delete', '*:modify', '*:read', '*:share'],
		requiredRoles: [UserRole.Employee],
		presetDescription: t('ProjectRoles.ownerPresetDescription', {
			context: verticalContextKey,
		}),
	};

	const teamMember: Preset = {
		rid: 'sharefile:permission_preset-teammember',
		name: t('ProjectRoles.counterpart'),
		permissions: [
			'*:read',
			'*:share',
			'file:create',
			'conversation:create',
			'requestlist:create',
			'requestlist:modify',
			'requestlist_item:create',
			'requestlist_item:modify',
			'requestlist_attachment:create',
			'requestlist_attachment:modify',
			'inforeq:create',
			'inforeq:modify',
			'infores:create',
			'infores:modify',
			'todo:create',
			'todo:modify',
			'datatable_record:create',
			'datatable_record:modify',
			'reviewapproval:create',
			'reviewapproval:modify',
			'reviewapproval:delete',
			'reviewapproval_revision:create',
			'reviewapproval_revision:modify',
			'reviewapproval_revision:delete',
		],
		requiredRoles: [UserRole.Employee],
		presetDescription: t('ProjectRoles.counterpartPresetDescription', {
			context: verticalContextKey,
		}),
	};

	const client: Preset = {
		rid: 'sharefile:permission_preset-client',
		name: t('ProjectRoles.fullView'),
		permissions: ['*:read', 'conversation:create', 'file:create'],
		presetDescription: t('ProjectRoles.fullViewPresetDescription', {
			context: verticalContextKey,
		}),
	};

	const limitedClient: Preset = {
		rid: 'sharefile:permission_preset-limited-client',
		name: t('ProjectRoles.limitedView'),
		permissions: ['project:read'],
		presetDescription: t('ProjectRoles.limitedViewPresetDescription'),
	};

	if (projectVersion && projectVersion > DataVersion.Original) {
		owner.permissions?.push('*:export');
		teamMember.permissions?.push('file:export');
		client.permissions?.push('file:export');
		teamMember.permissions?.push('folder:export');
		client.permissions?.push('folder:export');
	}

	teamMember.permissions?.push('folder:create');
	client.permissions?.push('folder:create');

	return [owner, teamMember, client, limitedClient];
};

const getFileExportPermissionSet = () => {
	const resourceType = 'folder';
	return {
		key: 'file-download',
		description: t('PermissionsConfig.files.canDownloadAll', { context: resourceType }),
		sourceLabel: t('FilePermissions.ShareEntity'),
		permissions: getFilePermissions('export'),
		requireOneOfKeys: ['file-view-all', 'project-view-all'],
	};
};

const getFilesPermissionSet = () => {
	const resourceType = 'folder';
	const filePermissionsSet = [
		{
			key: 'file-view-all',
			description: t('PermissionsConfig.files.canViewAll', { context: resourceType }),
			sourceLabel: t('FilePermissions.ShareEntity'),
			permissions: getFilePermissions('read'),
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'file-upload',
			description: t('PermissionsConfig.files.canUpload', { context: resourceType }),
			sourceLabel: t('FilePermissions.ShareEntity'),
			permissions: getFilePermissions('create'),
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'file-delete',
			description: t('PermissionsConfig.files.canDeleteAll', { context: resourceType }),
			sourceLabel: t('FilePermissions.ShareEntity'),
			permissions: getFilePermissions('delete'),
			requireOneOfKeys: ['file-view-all', 'project-view-all'],
			requiredRoles: [UserRole.Employee],
		},
	];
	return filePermissionsSet;
};

const getFilePermissions = (permissionType: string) => {
	return [`folder:${permissionType}`, `file:${permissionType}`];
};

const getAutomationsPermissionSet = () => {
	const isContextualAutomationsEnabled = getFeatureFlagValue(
		FeatureFlag.EnableAutomationPermissions
	);

	if (!isContextualAutomationsEnabled) {
		return [];
	}

	return [
		{
			key: 'flow-view-all',
			description: t('PermissionsConfig.automations.canViewAllAutomations'),
			sourceLabel: t('PermissionsConfig.automations.sourceLabel'),
			permissions: ['flow:read'],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'flow-create',
			description: t('PermissionsConfig.automations.canCreateAutomations'),
			sourceLabel: t('PermissionsConfig.automations.sourceLabel'),
			permissions: ['flow:create'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['project-view', 'project-view-all'],
		},
		{
			key: 'flow-modify-all',
			description: t('PermissionsConfig.automations.canModifyAllAutomations'),
			sourceLabel: t('PermissionsConfig.automations.sourceLabel'),
			permissions: ['flow:modify'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['flow-view-all', 'project-view-all'],
		},
		{
			key: 'flow-delete-all',
			description: t('PermissionsConfig.automations.canDeleteAllAutomations'),
			sourceLabel: t('PermissionsConfig.automations.sourceLabel'),
			permissions: ['flow:delete'],
			requiredRoles: [UserRole.Employee],
			requireOneOfKeys: ['flow-view-all', 'project-view-all'],
		},
	];
};
